import { Lock } from "lucide-react";
import { useState } from "react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuthSession } from "@/lib/session-context";

import { AppearanceSection } from "./AppearanceSection";
import { ChartsSection } from "./ChartsSection";
import { ModulesSection } from "./ModulesSection";
import { PREFERENCES_SECTIONS, type PreferencesSectionId } from "./preferences-sections";
import { PreferencesNav } from "./PreferencesNav";
import { UnitsSection } from "./UnitsSection";

/**
 * The user's own Preferences: a large two-pane dialog opened from the account
 * menu — sections down the left, the open section's choices on the right,
 * each previewed as it would look. Every choice saves as it's picked (no Save
 * button) to the user's server-side preferences, so the page behind the
 * dialog changes with it.
 *
 * Lives under the account menu, not behind a gear: a gear is a module's or
 * the platform's admin-only settings (`docs/POWERUP-SHELL.md` § "Settings"),
 * while these are per-user and ungated.
 */
export function PreferencesDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const session = useAuthSession();
  const [active, setActive] = useState<PreferencesSectionId>("appearance");
  const section = PREFERENCES_SECTIONS.find((s) => s.id === active) ?? PREFERENCES_SECTIONS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[min(780px,92vh)] max-h-none w-[min(1080px,calc(100vw-2rem))] max-w-none overflow-hidden p-0 sm:flex-row">
        <PreferencesNav active={active} onSelect={setActive} />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="border-b border-border bg-card px-6 py-5 pr-14">
            <h2 className="text-lg font-semibold text-foreground">{section.label}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
          </header>

          <div className="scrollbar-thin flex-1 overflow-y-auto bg-background-deep/60 px-6 py-5">
            {session.impersonating ? (
              <p className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground">
                <Lock size={14} strokeWidth={1.75} className="shrink-0" />
                You're viewing as {session.display_name}, so their preferences are shown read-only.
              </p>
            ) : null}
            {active === "appearance" && <AppearanceSection />}
            {active === "charts" && <ChartsSection />}
            {active === "units" && <UnitsSection />}
            {active === "modules" && <ModulesSection />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
